import type {
  LeaderboardResponse,
  LeaderboardQueryParams,
  LeaderboardDetailsResponse,
  LeaderboardDetailsQueryParams,
  TeamsListResponse,
  QualityGatesListResponse,
  TimeRange,
  GroupBy
} from '../../types/quality-gates'
import { devtoolsApiClient } from '../api/devtoolsApiClient'
import { logger } from '../../utils/logger'

const API_BASE = '/quality-gates'

/**
 * Leaderboard Service
 * Handles API calls for quality gates leaderboard functionality
 */
export const leaderboardService = {
  /**
   * Fetch leaderboard data with team/subTeam aggregation
   *
   * @param params - Query parameters for leaderboard
   * @returns LeaderboardResponse with aggregated team stats
   */
  async fetchLeaderboard(params: LeaderboardQueryParams = {}): Promise<LeaderboardResponse> {
    try {
      logger.log('Fetching leaderboard with params:', params)

      const queryParams: Record<string, string> = {}
      if (params.timeRange) queryParams.timeRange = params.timeRange
      if (params.groupBy) queryParams.groupBy = params.groupBy
      if (params.team) queryParams.team = params.team
      if (params.qualityGate) queryParams.qualityGate = params.qualityGate

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await devtoolsApiClient.get<any>(`${API_BASE}/leaderboard`, queryParams)

      logger.log('Leaderboard response:', response)

      // Handle different response structures
      const leaderboardData: LeaderboardResponse = {
        stats: response?.stats || response?.data?.stats || [],
        timeRange: response?.timeRange || response?.data?.timeRange || '7d',
        generatedAt:
          response?.generatedAt || response?.data?.generatedAt || new Date().toISOString(),
        totalPRs: response?.totalPRs || response?.data?.totalPRs || 0,
        overallPassRate: response?.overallPassRate || response?.data?.overallPassRate || 0
      }

      logger.log('Leaderboard fetched:', {
        teamsCount: leaderboardData.stats.length,
        totalPRs: leaderboardData.totalPRs,
        overallPassRate: leaderboardData.overallPassRate
      })

      return leaderboardData
    } catch (error) {
      logger.error('Failed to fetch leaderboard:', error)
      throw new Error('Failed to fetch leaderboard data. Please try again.')
    }
  },

  /**
   * Fetch list of available teams and subTeams for filter dropdowns
   *
   * @returns TeamsListResponse with unique teams and subTeams
   */
  async fetchTeams(): Promise<TeamsListResponse> {
    try {
      logger.log('Fetching teams list')

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await devtoolsApiClient.get<any>(`${API_BASE}/teams`)

      // Handle different response structures
      const teamsData: TeamsListResponse = {
        teams: response?.teams || response?.data?.teams || [],
        subTeams: response?.subTeams || response?.data?.subTeams || []
      }

      logger.log('Teams fetched:', {
        teamsCount: teamsData.teams.length,
        subTeamsCount: teamsData.subTeams.length
      })

      return teamsData
    } catch (error) {
      logger.error('Failed to fetch teams:', error)
      return { teams: [], subTeams: [] }
    }
  },

  /**
   * Fetch list of available quality gates for filter dropdown
   *
   * @returns QualityGatesListResponse with unique gate names
   */
  async fetchQualityGates(): Promise<QualityGatesListResponse> {
    try {
      logger.log('Fetching quality gates list')

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await devtoolsApiClient.get<any>(`${API_BASE}/gates`)

      // Handle different response structures
      const gatesData: QualityGatesListResponse = {
        gates: response?.gates || response?.data?.gates || []
      }

      logger.log('Quality gates fetched:', {
        gatesCount: gatesData.gates.length
      })

      return gatesData
    } catch (error) {
      logger.error('Failed to fetch quality gates:', error)
      return { gates: [] }
    }
  },

  /**
   * Fetch leaderboard details with per-quality-gate breakdown
   *
   * @param params - Query parameters (team required, subTeam and timeRange optional)
   * @returns LeaderboardDetailsResponse with per-gate stats
   */
  async fetchLeaderboardDetails(
    params: LeaderboardDetailsQueryParams
  ): Promise<LeaderboardDetailsResponse> {
    try {
      logger.log('Fetching leaderboard details with params:', params)

      const queryParams: Record<string, string> = {
        team: params.team
      }
      if (params.subTeam) queryParams.subTeam = params.subTeam
      if (params.qualityGate) queryParams.qualityGate = params.qualityGate
      if (params.timeRange) queryParams.timeRange = params.timeRange

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await devtoolsApiClient.get<any>(`${API_BASE}/leaderboard/details`, queryParams)

      logger.log('Leaderboard details response:', response)

      // Handle different response structures
      const detailsData: LeaderboardDetailsResponse = {
        team: response?.team || response?.data?.team || params.team,
        subTeam: response?.subTeam ?? response?.data?.subTeam ?? null,
        timeRange: response?.timeRange || response?.data?.timeRange || '7d',
        generatedAt:
          response?.generatedAt || response?.data?.generatedAt || new Date().toISOString(),
        totalPRs: response?.totalPRs || response?.data?.totalPRs || 0,
        gateStats: response?.gateStats || response?.data?.gateStats || []
      }

      logger.log('Leaderboard details fetched:', {
        team: detailsData.team,
        subTeam: detailsData.subTeam,
        totalPRs: detailsData.totalPRs,
        gatesCount: detailsData.gateStats.length
      })

      return detailsData
    } catch (error) {
      logger.error('Failed to fetch leaderboard details:', error)
      throw new Error('Failed to fetch leaderboard details. Please try again.')
    }
  },

  /**
   * Get display name for a team (format team name nicely)
   *
   * @param team - Team name (e.g. 'revex', 'platform', 'Global')
   * @returns Formatted display name (e.g. 'Revex', 'Platform', 'Unknown')
   */
  formatTeamName(team: string): string {
    if (!team || team === 'Global') return 'Unknown'
    // Capitalize first letter
    return team.charAt(0).toUpperCase() + team.slice(1)
  },

  /**
   * Get display name for a subTeam (format nicely)
   *
   * @param subTeam - SubTeam name (e.g. 'courses', 'lc-phone')
   * @returns Formatted display name (e.g. 'Courses', 'LC Phone', 'Unknown')
   */
  formatSubTeamName(subTeam: string | null): string {
    if (!subTeam) return 'Unknown'
    // Replace hyphens with spaces and capitalize each word
    return subTeam
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  },

  /**
   * Get color class for pass rate (for visual indicators)
   *
   * @param passRate - Pass rate percentage (0-100)
   * @returns Tailwind CSS color class
   */
  getPassRateColor(passRate: number): string {
    if (passRate >= 90) return 'text-green-600 dark:text-green-400'
    if (passRate >= 70) return 'text-yellow-600 dark:text-yellow-400'
    if (passRate >= 50) return 'text-orange-600 dark:text-orange-400'
    return 'text-red-600 dark:text-red-400'
  },

  /**
   * Get background color class for pass rate (for badges/chips)
   *
   * @param passRate - Pass rate percentage (0-100)
   * @returns Tailwind CSS background color class
   */
  getPassRateBgColor(passRate: number): string {
    if (passRate >= 90) return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
    if (passRate >= 70)
      return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
    if (passRate >= 50)
      return 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200'
    return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
  },

  /**
   * Get time range display label
   *
   * @param timeRange - Time range value (e.g. '7d')
   * @returns Human-readable label (e.g. 'Last 7 Days')
   */
  getTimeRangeLabel(timeRange: TimeRange): string {
    const labels: Record<TimeRange, string> = {
      '7d': 'Last 7 Days',
      '14d': 'Last 14 Days',
      '30d': 'Last 30 Days'
    }
    return labels[timeRange] || 'Last 7 Days'
  },

  /**
   * Get group by display label
   *
   * @param groupBy - Group by value (e.g. 'team')
   * @returns Human-readable label (e.g. 'By Team')
   */
  getGroupByLabel(groupBy: GroupBy): string {
    const labels: Record<GroupBy, string> = {
      team: 'By Team',
      subTeam: 'By Sub-Team'
    }
    return labels[groupBy] || 'By Team'
  },

  /**
   * Calculate rank suffix (1st, 2nd, 3rd, etc.)
   *
   * @param rank - Numeric rank
   * @returns Rank with suffix (e.g. '1st', '2nd', '3rd')
   */
  getRankSuffix(rank: number): string {
    const j = rank % 10
    const k = rank % 100
    if (j === 1 && k !== 11) return `${rank}st`
    if (j === 2 && k !== 12) return `${rank}nd`
    if (j === 3 && k !== 13) return `${rank}rd`
    return `${rank}th`
  },

  /**
   * Format gate name for display (e.g., "hardcoded_api_keys" -> "Hardcoded Api Keys")
   *
   * @param gateName - Gate name (script name)
   * @returns Formatted display name
   */
  formatGateName(gateName: string): string {
    if (!gateName) return 'Unknown'
    return gateName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  },

  /**
   * Get progress bar width as percentage string
   *
   * @param passRate - Pass rate percentage (0-100)
   * @returns Width percentage string for styling
   */
  getProgressBarWidth(passRate: number): string {
    return `${Math.max(0, Math.min(100, passRate))}%`
  },

  /**
   * Get progress bar color class based on pass rate
   *
   * @param passRate - Pass rate percentage (0-100)
   * @returns Tailwind CSS background color class for progress bar
   */
  getProgressBarColor(passRate: number): string {
    if (passRate >= 90) return 'bg-green-500'
    if (passRate >= 70) return 'bg-yellow-500'
    if (passRate >= 50) return 'bg-orange-500'
    return 'bg-red-500'
  }
}
